import {
  type DirectoryPerson,
  parseMarkdown,
  selectDirectory,
  selectDisplayName,
  sortedRoles,
  t,
  toPlainText,
} from '@alternativamais/chat-core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '../../../componentes/Avatar';
import { avisarErro } from '../../../componentes/Avisos';
import {
  CaixaMenu,
  Flutuante,
  ItemMenu,
  type Posicao,
} from '../../../componentes/Flutuante';
import { Icone } from '../../../componentes/Icone';
import { abrirCamada } from '../../../chat/camadas';
import { useCliente, useDerivado, useEstado } from '../../../chat/contexto';
import {
  useAgora,
  useCorDoNome,
  useModoDesenvolvedor,
} from '../../../chat/ganchos';
import { filtrarPorTermo } from '@alternativamais/chat-core';
import { BotaoCaixa } from '../cabecalho/Cabecalho';
import {
  avisarCopiado,
  TextoDoStatus,
  useStatusPersonalizado,
} from '../perfil/perfil';
import { BotaoGaveta } from '../../../chat/gaveta';

type Aba = 'online' | 'todos' | 'setor';

/** Quanto tempo alguém conta como "ativo agora" depois de escrever. */
const ATIVO_MS = 30 * 60_000;

/**
 * Início das mensagens diretas (D1): as pessoas da empresa no lugar dos
 * amigos do Discord (online, todos, por setor), com a busca, "Nova
 * conversa" e o "Ativo agora" de quem escreveu nos canais há pouco.
 */
export function Pessoas() {
  const [aba, setAba] = useState<Aba>('online');
  const [busca, setBusca] = useState('');
  const pessoas = useDerivado(selectDirectory, (estado) => [
    estado.members,
    estado.users,
    estado.presences,
    estado.guildIds,
  ]);
  const setores = useDerivado(
    (estado) => {
      const mapa: Record<string, string> = {};
      for (const pessoa of pessoas) {
        if (!pessoa.guildId) continue;
        const membro = estado.members[pessoa.guildId]?.[pessoa.userId];
        const cargo = membro
          ? sortedRoles(estado, pessoa.guildId).find(
              (item) => item.hoist && membro.roles.includes(item.id),
            )
          : undefined;
        mapa[pessoa.userId] = cargo?.name ?? 'Sem setor';
      }
      return mapa;
    },
    (estado) => [pessoas, estado.guilds],
  );

  const lista = useMemo(() => {
    const termo = busca.trim();
    let base =
      aba === 'online'
        ? pessoas.filter((item) => item.status !== 'offline')
        : pessoas;
    if (termo)
      base = filtrarPorTermo(base, termo, (pessoa) => [pessoa.name], 500);
    return base;
  }, [aba, busca, pessoas]);

  const grupos = useMemo(() => {
    if (aba !== 'setor') {
      return [
        {
          titulo: `${aba === 'online' ? 'Online' : 'Todos'} — ${lista.length}`,
          pessoas: lista,
        },
      ];
    }
    const porSetor = new Map<string, DirectoryPerson[]>();
    for (const pessoa of lista) {
      const setor = setores[pessoa.userId] ?? 'Sem setor';
      porSetor.set(setor, [...(porSetor.get(setor) ?? []), pessoa]);
    }
    const collator = new Intl.Collator('pt-BR', { sensitivity: 'base' });
    return [...porSetor.entries()]
      .sort(([a], [b]) =>
        a === 'Sem setor' ? 1 : b === 'Sem setor' ? -1 : collator.compare(a, b),
      )
      .map(([setor, membros]) => ({
        titulo: `${setor} — ${membros.length}`,
        pessoas: membros,
      }));
  }, [aba, lista, setores]);

  return (
    <div className="flex min-w-0 grow flex-col bg-chat">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-divisor pr-3 pl-4">
        <BotaoGaveta />
        <Icone nome="pessoas" tamanho={22} className="text-texto-3" />
        <h1 className="m-0 text-base font-semibold whitespace-nowrap">
          Pessoas
        </h1>
        <span className="h-6 w-px shrink-0 bg-borda" />
        <div
          role="tablist"
          aria-label="Filtrar pessoas"
          className="flex items-center gap-1.5"
        >
          {(
            [
              ['online', 'Online'],
              ['todos', 'Todos'],
              ['setor', 'Por setor'],
            ] as const
          ).map(([valor, rotulo]) => (
            <button
              key={valor}
              type="button"
              role="tab"
              aria-selected={aba === valor}
              onClick={() => setAba(valor)}
              className={`h-7 rounded-md px-2.5 text-[15px] ${
                aba === valor
                  ? 'bg-ativo font-semibold text-texto'
                  : 'font-medium text-texto-2 hover:bg-hover hover:text-texto'
              }`}
            >
              {rotulo}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => abrirCamada({ tipo: 'nova-conversa' })}
          className="ml-1.5 flex h-7 items-center gap-1.5 rounded-md bg-destaque pr-3 pl-2 text-sm font-semibold whitespace-nowrap text-destaque-texto hover:brightness-105"
        >
          <Icone nome="mais" tamanho={16} />
          Nova conversa
        </button>
        <span className="grow" />
        <BotaoCaixa />
      </header>
      <div className="flex min-h-0 grow">
        <main
          aria-label="Pessoas"
          className="rolagem flex min-w-0 grow flex-col overflow-y-auto px-6 pt-4 pb-6"
        >
          <label className="relative shrink-0">
            <span className="sr-only">Buscar pessoas</span>
            <input
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Buscar pessoas"
              className="h-9 w-full rounded-lg border-0 bg-rail pr-10 pl-3 text-[15px] text-texto outline-none placeholder:text-texto-3 focus:outline focus:outline-destaque"
            />
            <Icone
              nome="busca"
              tamanho={18}
              className="absolute top-2 right-3 text-texto-3"
            />
          </label>
          {lista.length === 0 ? (
            <p className="mt-10 text-center text-[15px] text-texto-3">
              {busca.trim()
                ? 'Ninguém com esse nome.'
                : aba === 'online'
                  ? 'Ninguém online agora.'
                  : 'Nenhuma pessoa nos seus servidores.'}
            </p>
          ) : (
            grupos.map((grupo) => (
              <section key={grupo.titulo} aria-label={grupo.titulo}>
                <h2 className="mt-6 mb-2 ml-2 text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
                  {grupo.titulo}
                </h2>
                <ul className="m-0 list-none p-0">
                  {grupo.pessoas.map((pessoa) => (
                    <LinhaPessoa key={pessoa.userId} pessoa={pessoa} />
                  ))}
                </ul>
              </section>
            ))
          )}
        </main>
        <AtivoAgora />
      </div>
    </div>
  );
}

function LinhaPessoa({ pessoa }: { pessoa: DirectoryPerson }) {
  const dev = useModoDesenvolvedor();
  const cliente = useCliente();
  const navegar = useNavigate();
  const usuario = useEstado((estado) => estado.users[pessoa.userId]);
  const cor = useCorDoNome(pessoa.userId, pessoa.guildId);
  const personalizado = useStatusPersonalizado(pessoa.userId);
  const [menu, setMenu] = useState<Posicao | null>(null);
  if (!usuario) return null;
  const conversar = () =>
    cliente
      .openDm(pessoa.userId)
      .then((canal) => navegar(`/canais/@me/${canal.id}`))
      .catch(avisarErro);
  return (
    <li className="group flex h-[60px] items-center gap-3 rounded-lg border-t border-borda px-2 hover:border-transparent hover:bg-hover">
      <button
        type="button"
        aria-label={`Perfil de ${pessoa.name}`}
        onClick={(evento) => {
          const caixa = evento.currentTarget.getBoundingClientRect();
          abrirCamada({
            tipo: 'perfil',
            userId: pessoa.userId,
            guildId: pessoa.guildId,
            ancora: { x: caixa.right + 8, y: caixa.top },
          });
        }}
        className="rounded-full"
      >
        <Avatar
          pessoa={usuario}
          nome={pessoa.name}
          tamanho={32}
          status={pessoa.status}
        />
      </button>
      <button
        type="button"
        onClick={() => void conversar()}
        className="flex min-w-0 grow flex-col gap-px text-left"
      >
        <span
          className="truncate text-[15px] font-semibold"
          style={cor ? { color: cor } : undefined}
        >
          {pessoa.name}
        </span>
        <span className="truncate text-[13px] text-texto-3">
          {personalizado ? (
            <TextoDoStatus userId={pessoa.userId} />
          ) : (
            t(`status.${pessoa.status}`)
          )}
        </span>
      </button>
      <button
        type="button"
        aria-label={`Enviar mensagem para ${pessoa.name}`}
        title="Enviar mensagem"
        onClick={() => void conversar()}
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-elevado text-texto-2 hover:text-texto"
      >
        <Icone nome="responder" tamanho={18} />
      </button>
      <button
        type="button"
        aria-label={`Mais opções de ${pessoa.name}`}
        aria-haspopup="menu"
        onClick={(evento) => {
          const caixa = evento.currentTarget.getBoundingClientRect();
          setMenu(menu ? null : { x: caixa.right, y: caixa.bottom + 4 });
        }}
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-elevado text-texto-2 hover:text-texto"
      >
        <Icone nome="mais-opcoes" tamanho={18} />
      </button>
      {menu && (
        <Flutuante
          ancora={menu}
          aoFechar={() => setMenu(null)}
          rotulo={`Opções de ${pessoa.name}`}
          papel="menu"
          alinhar="fim"
        >
          <CaixaMenu largura={220}>
            <ItemMenu
              icone="usuario"
              aoEscolher={() => {
                setMenu(null);
                abrirCamada({
                  tipo: 'perfil',
                  userId: pessoa.userId,
                  guildId: pessoa.guildId,
                  ancora: menu,
                });
              }}
            >
              Ver perfil
            </ItemMenu>
            <ItemMenu
              icone="copiar"
              aoEscolher={() => {
                setMenu(null);
                avisarCopiado(usuario.username, 'Nome de usuário copiado.');
              }}
            >
              Copiar nome de usuário
            </ItemMenu>
            {dev && (
              <ItemMenu
                icone="copiar"
                aoEscolher={() => {
                  setMenu(null);
                  avisarCopiado(pessoa.userId, 'ID copiado.');
                }}
              >
                Copiar ID
              </ItemMenu>
            )}
          </CaixaMenu>
        </Flutuante>
      )}
    </li>
  );
}

type Atividade = {
  userId: string;
  guildId: string | null;
  canalId: string;
  mensagemId: string;
  quando: number;
  texto: string;
};

/** Quem escreveu nos canais carregados há pouco (o "Ativo agora" do design). */
function AtivoAgora() {
  const agora = useAgora(60_000);
  const atividades = useDerivado(
    (estado): Atividade[] => {
      const ultimas = new Map<string, Atividade>();
      for (const [canalId, lista] of Object.entries(estado.messages)) {
        const canal = estado.channels[canalId];
        if (!canal?.guild_id) continue;
        for (const id of lista.ids.slice(-30)) {
          const mensagem = lista.byId[id];
          const autor = mensagem?.author;
          if (
            !autor ||
            autor.bot ||
            autor.id === estado.me?.id ||
            mensagem.webhook_id
          )
            continue;
          const quando = Date.parse(mensagem.timestamp);
          if (agora - quando > ATIVO_MS) continue;
          const anterior = ultimas.get(autor.id);
          if (anterior && anterior.quando >= quando) continue;
          ultimas.set(autor.id, {
            userId: autor.id,
            guildId: canal.guild_id,
            canalId,
            mensagemId: mensagem.id,
            quando,
            texto: mensagem.content
              ? toPlainText(parseMarkdown(mensagem.content, { inline: true }), {
                  user: (pessoa) =>
                    selectDisplayName(estado, pessoa, canal.guild_id),
                  role: () => 'cargo',
                  channel: (outro) => estado.channels[outro]?.name ?? 'canal',
                })
              : mensagem.attachments.length
                ? 'Enviou um anexo.'
                : '',
          });
        }
      }
      return [...ultimas.values()]
        .sort((a, b) => b.quando - a.quando)
        .slice(0, 6);
    },
    (estado) => [estado.messages, Math.floor(agora / 60_000)],
  );
  return (
    <aside
      aria-labelledby="ativo-agora"
      className="rolagem flex w-[360px] shrink-0 flex-col gap-4 overflow-y-auto border-l border-borda p-4 max-[1100px]:hidden"
    >
      <h2 id="ativo-agora" className="mt-1 mb-0 font-titulo text-xl font-bold">
        Ativo agora
      </h2>
      {atividades.length === 0 ? (
        <p className="m-0 text-sm leading-normal text-texto-3">
          Quem está conversando nos canais agora e o status de quem trabalha com
          você aparecem aqui.
        </p>
      ) : (
        atividades.map((atividade) => (
          <CartaoAtivo
            key={atividade.userId}
            atividade={atividade}
            agora={agora}
          />
        ))
      )}
    </aside>
  );
}

function CartaoAtivo({
  atividade,
  agora,
}: {
  atividade: Atividade;
  agora: number;
}) {
  const cliente = useCliente();
  const navegar = useNavigate();
  const usuario = useEstado((estado) => estado.users[atividade.userId]);
  const nome = useEstado((estado) =>
    selectDisplayName(estado, atividade.userId, atividade.guildId),
  );
  const status = useEstado(
    (estado) => estado.presences[atividade.userId]?.status ?? 'offline',
  );
  const canal = useEstado((estado) => estado.channels[atividade.canalId]);
  const servidor = useEstado((estado) =>
    atividade.guildId ? estado.guilds[atividade.guildId]?.name : null,
  );
  if (!usuario || !canal) return null;
  const minutos = Math.max(1, Math.round((agora - atividade.quando) / 60_000));
  return (
    <article
      aria-label={`${nome} escreveu em #${canal.name}`}
      className="flex flex-col gap-3 rounded-lg bg-elevado p-4"
    >
      <div className="flex items-center gap-3">
        <Avatar
          pessoa={usuario}
          nome={nome}
          tamanho={40}
          status={status}
          fundo="var(--cor-elevado)"
        />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-[15px] font-semibold">{nome}</span>
          <span className="flex items-center gap-1.5 text-[13px] whitespace-nowrap text-texto-3">
            <Icone nome="hash" tamanho={14} />
            escreveu em #{canal.name} há {minutos} min
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={() =>
          navegar(
            `/canais/${atividade.guildId}/${atividade.canalId}/${atividade.mensagemId}`,
          )
        }
        className="flex flex-col gap-1 rounded-lg bg-chat px-3 py-2.5 text-left hover:bg-hover"
      >
        <span className="text-xs font-semibold text-texto-3">
          #{canal.name}
          {servidor ? ` · ${servidor}` : ''}
        </span>
        {atividade.texto && (
          <span className="line-clamp-2 text-sm leading-[1.4]">
            {atividade.texto}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={() =>
          cliente
            .openDm(atividade.userId)
            .then((dm) => navegar(`/canais/@me/${dm.id}`))
            .catch(avisarErro)
        }
        className="flex h-9 items-center justify-center gap-2 rounded-lg bg-ativo text-sm font-semibold hover:brightness-110"
      >
        <Icone nome="responder" tamanho={16} />
        Enviar mensagem
      </button>
    </article>
  );
}
